import { useMemo, useState } from 'react'
import { getPageHeader } from '@/constants'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { Button } from '@/components/ui/button'
import type { FilterOption } from '@/components/filters/filter-menu'
import { FloatingPageSection } from '@/components/filters/floating-page-section'
import { PaneListEmptyState } from '@/components/layout/pane-list-empty-state'
import { PaneListError, PaneListLoading } from '@/components/layout/pane-list-section'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import { Brain } from 'lucide-react'
import { useMemories, Memory, type MemoryUpdate } from '@/hooks/use-memories'
import { MemoryCard } from './memory-card'
import { MemoryEditModal } from './memory-edit-modal'
import { MemoryGraph } from './memory-graph'
import { useMemoriesFilterBar } from './use-memories-filter-bar'
import { MEMORIES_DEFAULT_SORT, MEMORIES_SORT_OPTIONS } from '@/lib/list-filters/memories'
import { transformMemoriesToFlow } from '@/utils/graph-data'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const IMPORTANCE_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const memoriesPageHeader = (title: string) => (
  <PageHeader
    leading={<PageHeaderLeading />}
    title={title}
    icon={<Brain className="h-5 w-5" />}
  />
)

export function MemoriesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [memoryToDelete, setMemoryToDelete] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'graph'>('list')

  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [importanceFilter, setImportanceFilter] = useState<string>('all')
  const [selectedEntity, setSelectedEntity] = useState<Array<{ id: string; name: string }>>([])
  const [sortBy, setSortBy] = useState<string>(MEMORIES_DEFAULT_SORT)

  const itemsPerPage = 20

  const { memories, isLoading, error, updateMemory, deleteMemory, isUpdating, isDeleting } = useMemories({
    category: categoryFilter && categoryFilter !== 'all' ? categoryFilter : undefined,
    importance: importanceFilter && importanceFilter !== 'all' ? importanceFilter : undefined,
    entity_id: selectedEntity.length === 1 ? selectedEntity[0].id : undefined,
    sort_by: sortBy,
  })

  const selectedEntityIds = selectedEntity.map((e) => e.id)
  const filteredMemories = memories.filter((memory) => {
    const q = searchTerm.trim().toLowerCase()
    const matchesSearch =
      q === '' ||
      memory.content.toLowerCase().includes(q) ||
      memory.category.toLowerCase().includes(q) ||
      memory.subcategory.toLowerCase().includes(q) ||
      memory.entities?.some((e) => e.name.toLowerCase().includes(q))

    const matchesEntities =
      selectedEntityIds.length === 0 ||
      (selectedEntityIds.length === 1
        ? true
        : memory.entities?.some((e) => selectedEntityIds.includes(e.id)))

    return matchesSearch && matchesEntities
  })

  const categories = useMemo(
    () => Array.from(new Set(memories.map((m) => m.category))).sort(),
    [memories],
  )

  const categoryOptions: FilterOption[] = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...categories.map((cat) => ({ value: cat, label: cat })),
    ],
    [categories],
  )

  const resetPage = () => setCurrentPage(1)

  const { filterBar, hasActiveFilters } = useMemoriesFilterBar({
    searchTerm,
    setSearchTerm,
    categoryFilter,
    setCategoryFilter,
    importanceFilter,
    setImportanceFilter,
    sortBy,
    setSortBy,
    selectedEntity,
    setSelectedEntity,
    categoryOptions,
    importanceOptions: IMPORTANCE_OPTIONS,
    sortOptions: MEMORIES_SORT_OPTIONS,
    viewMode,
    setViewMode,
    sortDisabled: viewMode === 'graph',
    onPageReset: resetPage,
  })

  const totalPages = Math.ceil(filteredMemories.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedMemories = filteredMemories.slice(startIndex, startIndex + itemsPerPage)

  const handleEdit = (memory: Memory) => {
    setSelectedMemory(memory)
    setIsEditModalOpen(true)
  }

  const handleSave = async (memoryId: string, updates: MemoryUpdate) => {
    try {
      await updateMemory({ memoryId, updates })
      setIsEditModalOpen(false)
      setSelectedMemory(null)
    } catch (err) {
      console.error('Failed to update memory:', err)
    }
  }

  const handleDeleteClick = (memoryId: string) => {
    setMemoryToDelete(memoryId)
  }

  const handleDeleteConfirm = async () => {
    if (memoryToDelete) {
      try {
        await deleteMemory(memoryToDelete)
        setMemoryToDelete(null)
      } catch (err) {
        console.error('Failed to delete memory:', err)
      }
    }
  }

  const handleEntityClick = (entityId: string, entityName: string) => {
    if (!selectedEntity.some((e) => e.id === entityId)) {
      setSelectedEntity((prev) => [...prev, { id: entityId, name: entityName }])
      setCurrentPage(1)
    }
  }

  const { title } = getPageHeader('/memories', 'Memories', '')

  const emptyState = (
    <PaneListEmptyState
      icon={<Brain className="h-12 w-12" />}
      title={hasActiveFilters ? 'No memories match your filters' : 'No memories found'}
      description={
        hasActiveFilters
          ? 'Try adjusting your search or filters.'
          : 'Memories from your conversations will appear here.'
      }
    />
  )

  const modals = (
    <>
      <MemoryEditModal
        memory={selectedMemory}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedMemory(null)
        }}
        onSave={handleSave}
        isSaving={isUpdating}
      />

      <AlertDialog open={!!memoryToDelete} onOpenChange={() => setMemoryToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete memory?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this memory? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  if (viewMode === 'graph') {
    return (
      <>
        <PageContainer maxWidth="full" padding="p-0" contained={false} className="!pb-0">
          <div className="relative min-h-[calc(100dvh-4rem)] w-full overflow-hidden">
            {!isLoading && !error && filteredMemories.length > 0 ? (
              <MemoryGraph
                className="absolute inset-0"
                graphData={transformMemoriesToFlow(filteredMemories)}
                onMemoryClick={handleEdit}
                onMemoryEdit={handleEdit}
                onMemoryDelete={handleDeleteClick}
              />
            ) : null}

            <FloatingPageSection header={memoriesPageHeader(title)}>{filterBar}</FloatingPageSection>

            {isLoading ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <div className="rounded-lg border border-border/40 bg-background/90 p-6 shadow-sm backdrop-blur">
                  <PaneListLoading message="Loading memories…" />
                </div>
              </div>
            ) : null}

            {!isLoading && error ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
                <div className="max-w-md rounded-lg border border-border/40 bg-background/90 p-6 shadow-sm backdrop-blur">
                  <PaneListError message={`Failed to load memories: ${error.message}`} />
                </div>
              </div>
            ) : null}

            {!isLoading && !error && filteredMemories.length === 0 ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
                <div className="rounded-lg border border-border/40 bg-background/90 p-6 shadow-sm backdrop-blur">
                  {emptyState}
                </div>
              </div>
            ) : null}
          </div>
          </PageContainer>
        {modals}
      </>
    )
  }

  return (
    <>
      <PageContainer>
        <PageHeaderStack compact>
            {memoriesPageHeader(title)}
            {filterBar}
          </PageHeaderStack>

          {isLoading ? (
            <PaneListLoading message="Loading memories…" />
          ) : error ? (
            <PaneListError message={`Failed to load memories: ${error.message}`} />
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {filteredMemories.length}{' '}
                {filteredMemories.length === 1 ? 'memory' : 'memories'}
              </p>
              {paginatedMemories.length === 0 ? (
                emptyState
              ) : (
                <PaneResponsiveCardGrid>
                  {paginatedMemories.map((memory) => (
                    <MemoryCard
                      key={memory.id}
                      memory={memory}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      onEntityClick={handleEntityClick}
                      isDeleting={isDeleting}
                    />
                  ))}
                </PaneResponsiveCardGrid>
              )}

              {totalPages > 1 ? (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1}–
                    {Math.min(startIndex + itemsPerPage, filteredMemories.length)} of{' '}
                    {filteredMemories.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="px-2 text-sm tabular-nums">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </PageContainer>
      {modals}
    </>
  )
}
